// 화살·장판(c022 라이라): 세계 투사체 화살과 바닥 장판(화살비·그물 덫·회오리). 영웅 시간(히트스톱·일시정지 동안 멈춤)으로 진행한다.
// 화살은 발사 순간의 위치·방향으로 나가 매 틱 이동 구간을 판정한다(검기 wave와 같은 방식, 벽에서 멈춤). 관통이 아니면 처음 맞은 적에서 멈춘다.
// 캔슬·회피·피격으로 영웅 동작이 바뀌어도 날아간다. 판 초기화·장면 전환·사망 때 모두 지운다.
// 슬롯은 모두 미리 만든다(프레임 루프 안에서 할당하지 않는다).
import { FIXED_DT, HERO } from "../shared/config";
import { Ev } from "../shared/events";
import { heroSkills } from "../shared/skills/registry";
import type { ArrowKindId, ArrowSpec, LaunchGrant, SkillCue, ZoneSpec } from "../shared/skills/types";
import { BossState, MobState, Scene, combatScene } from "../shared/types";
import { HitSource, type HitMark, type HitSourceId } from "./combat";
import type { GameSim } from "./game";

/** 동시에 날아가는 화살 수 상한(3연사·유도 6발·기본 공격이 겹쳐도 남는다). 꽉 차면 새 화살은 쏘지 않는다 */
export const ARROW_SLOTS = 24;
/** 동시에 깔린 장판 수 상한 */
export const ZONE_SLOTS = 8;
/** 벽 검사 간격(m) */
const WALL_STEP = 0.25;
/** 화살 기본 발사 높이(m) */
const ARROW_Y = 1.2;
/** 유도 화살이 최근에 겨눈 대상 기억 수(같은 적에게 몰리지 않게) */
const HOMING_MEMORY = 6;

export const ArrowEndReason = { Range: 0, Hit: 1, Wall: 2 } as const;
const ZoneKind = { rain: 0, net: 1, vortex: 2 } as const;

interface Arrow {
  active: boolean;
  x: number; z: number; dx: number; dz: number;
  /** 틱당 이동(m) */
  step: number;
  range: number; width: number; pierce: boolean; kind: ArrowKindId;
  mult: number; source: HitSourceId; finisher: boolean;
  launch: LaunchGrant | null;
  explode: { readonly radius: number; readonly mult: number } | null;
  vortex: ZoneSpec | null;
  /** 이 화살을 쏜 스킬 번호(0 = 기본 공격). 세계 큐를 고른다 */
  skillId: number;
  front: number;
  readonly mark: HitMark;
}

interface Zone {
  active: boolean;
  kind: number;
  x: number; z: number; radius: number;
  pulsesLeft: number; every: number; timer: number;
  mult: number;
  /** net: 남은 대기 틱·밟힘 반경·속박 틱 */
  life: number; trigger: number; root: number;
  pull: number;
  skillId: number;
  readonly mark: HitMark;
}

const freshMark = (n: number): HitMark => ({ mobs: new Uint8Array(n), boss: false, rewards: 0, kills: 0, mass: false, mp: false, linkStage: 0 });

export class Arrows {
  readonly arrows: Arrow[] = [];
  readonly zones: Zone[] = [];
  private readonly homing = new Int16Array(HOMING_MEMORY).fill(-1);
  private homingAt = 0;

  constructor(private readonly g: GameSim) {
    const n = g.mobs.capacity;
    for (let i = 0; i < ARROW_SLOTS; i++) {
      this.arrows.push({ active: false, x: 0, z: 0, dx: 0, dz: 1, step: 0, range: 0, width: 0, pierce: false, kind: 0, mult: 0, source: HitSource.Basic, finisher: false, launch: null, explode: null, vortex: null, skillId: 0, front: 0, mark: freshMark(n) });
    }
    for (let i = 0; i < ZONE_SLOTS; i++) {
      this.zones.push({ active: false, kind: 0, x: 0, z: 0, radius: 0, pulsesLeft: 0, every: 1, timer: 0, mult: 0, life: 0, trigger: 0, root: 0, pull: 0, skillId: 0, mark: freshMark(n) });
    }
  }

  /** 판 초기화·장면 전환·사망: 날아가는 화살과 깔린 장판을 모두 지운다 */
  clear(): void {
    for (const a of this.arrows) a.active = false;
    for (const z of this.zones) z.active = false;
    this.homing.fill(-1); this.homingAt = 0;
  }

  /** 지금 날아가는 화살 수(테스트·QA) */
  get liveArrows(): number { let n = 0; for (const a of this.arrows) if (a.active) n++; return n; }
  get liveZones(): number { let n = 0; for (const z of this.zones) if (z.active) n++; return n; }

  /**
   * 화살 발사(영웅 위치·facing 기준). spec.count발을 spreadDeg 부채꼴로, 유도면 각 발이 반경 안 서로 다른 적을 겨눈다.
   * skillId 0 = 기본 공격(source Basic), 그 밖 = 스킬. 한 발마다 Ev.Arrow
   */
  fire(spec: ArrowSpec, mult: number, skillId: number, finisher: boolean, launch: LaunchGrant | null): void {
    const h = this.g.hero, count = Math.max(1, spec.count ?? 1);
    const spread = ((spec.spreadDeg ?? 0) * Math.PI) / 180;
    for (let k = 0; k < count; k++) {
      let dir = h.facing + (count > 1 ? spread * (k / (count - 1) - 0.5) : 0);
      if (spec.homingRadius) dir = this.homingDir(h.x, h.z, spec.homingRadius, dir);
      this.launch(h.x, h.z, h.y + (spec.y ?? ARROW_Y), dir, spec, mult, skillId, finisher, launch);
    }
  }

  private launch(x: number, z: number, y: number, dir: number, spec: ArrowSpec, mult: number, skillId: number, finisher: boolean, launch: LaunchGrant | null): void {
    let slot = -1;
    for (let i = 0; i < this.arrows.length; i++) if (!this.arrows[i].active) { slot = i; break; }
    if (slot < 0) return;
    const a = this.arrows[slot], g = this.g;
    a.active = true; a.x = x; a.z = z; a.dx = Math.sin(dir); a.dz = Math.cos(dir);
    a.step = spec.speed * FIXED_DT; a.range = spec.range; a.width = spec.width; a.pierce = spec.pierce; a.kind = spec.kind;
    a.mult = mult; a.source = skillId === 0 ? HitSource.Basic : HitSource.Skill; a.finisher = finisher; a.launch = launch;
    a.explode = spec.explode ?? null; a.vortex = spec.vortex ?? null; a.skillId = skillId; a.front = 0;
    const mk = a.mark;
    mk.mobs.fill(0); mk.boss = false; mk.rewards = 0; mk.kills = 0; mk.mass = false; mk.mp = false;
    // 연계 단계는 발사 순간 값으로 고정한다(검기와 같다)
    mk.linkStage = g.link.stage;
    const e = g.emit(Ev.Arrow);
    e.x = x; e.y = y; e.z = z; e.a = dir; e.b = spec.speed; e.c = spec.range; e.d = spec.kind;
    e.flags = (spec.pierce ? 1 : 0) | (skillId !== 0 ? 2 : 0) | (slot << 8);
  }

  /** 유도 화살 방향: 반경 안에서 최근에 겨누지 않은 가장 가까운 적(없으면 가장 가까운 적, 그것도 없으면 fallback) */
  private homingDir(x: number, z: number, radius: number, fallback: number): number {
    const g = this.g, m = g.mobs;
    let best = radius, id = -1, any = -1, anyD = radius;
    for (let i = 0; i < m.capacity; i++) {
      const st = m.state[i];
      if (st === MobState.Inactive || st === MobState.Dying || st === MobState.Spawning || st === MobState.Wakeup) continue;
      const d = Math.hypot(m.x[i] - x, m.z[i] - z);
      if (d < anyD) { anyD = d; any = i; }
      let recent = false;
      for (let k = 0; k < HOMING_MEMORY; k++) if (this.homing[k] === i) { recent = true; break; }
      if (!recent && d < best) { best = d; id = i; }
    }
    const pick = id >= 0 ? id : any;
    if (pick < 0) {
      const b = g.boss;
      if (g.bossController.vulnerable && Math.hypot(b.x - x, b.z - z) <= radius) return Math.atan2(b.x - x, b.z - z);
      return fallback;
    }
    this.homing[this.homingAt] = pick; this.homingAt = (this.homingAt + 1) % HOMING_MEMORY;
    const dx = m.x[pick] - x, dz = m.z[pick] - z;
    return dx === 0 && dz === 0 ? fallback : Math.atan2(dx, dz);
  }

  /**
   * 장판 놓기(영웅 기준 위치). at = target이면 자석 대상 자리(없으면 전방 offset), front = 전방 offset, hero = 발밑.
   * 놓는 순간 세계 큐(place)를 낸다
   */
  place(spec: ZoneSpec, mult: number, skillId: number): void {
    const g = this.g, h = g.hero;
    let x = h.x + Math.sin(h.facing) * spec.offset, z = h.z + Math.cos(h.facing) * spec.offset;
    if (spec.at === 'hero') { x = h.x; z = h.z; }
    else if (spec.at === 'target') {
      const mg = g.magnet;
      if (mg.boss) { x = g.boss.x; z = g.boss.z; }
      else if (mg.target >= 0) { x = g.mobs.x[mg.target]; z = g.mobs.z[mg.target]; }
    }
    this.placeAt(spec, mult, skillId, x, z);
  }

  private placeAt(spec: ZoneSpec, mult: number, skillId: number, x: number, z: number): void {
    let zn: Zone | undefined;
    for (const s of this.zones) if (!s.active) { zn = s; break; }
    if (!zn) return;
    zn.active = true; zn.kind = ZoneKind[spec.kind]; zn.x = x; zn.z = z; zn.radius = spec.radius;
    zn.pulsesLeft = Math.max(1, spec.pulses); zn.every = Math.max(1, spec.every); zn.timer = zn.every;
    zn.mult = mult; zn.life = spec.life ?? 0; zn.trigger = spec.trigger ?? 0; zn.root = spec.root ?? 0; zn.pull = spec.pull ?? 0; zn.skillId = skillId;
    const mk = zn.mark;
    mk.mobs.fill(0); mk.boss = false; mk.rewards = 0; mk.kills = 0; mk.mass = false; mk.mp = false; mk.linkStage = this.g.link.stage;
    this.worldCue(skillId, 'place', x, z);
  }

  /** 영웅 시간 한 틱(히트스톱·일시정지에서는 호출되지 않는다). 영웅 동작 처리 전에 부른다 */
  step(): void {
    if (!combatScene(this.g.flow.scene)) return;
    for (const zn of this.zones) if (zn.active) this.advanceZone(zn);
    for (const a of this.arrows) if (a.active) this.advance(a);
  }

  // ── 내부 ────────────────────────────────────────

  private advance(a: Arrow): void {
    const g = this.g, prev = a.front, target = Math.min(a.range, prev + a.step);
    let reach = target, blocked = false;
    for (let p = Math.min(prev + WALL_STEP, target); ; p = Math.min(p + WALL_STEP, target)) {
      if (!g.walkable(a.x + a.dx * p, a.z + a.dz * p, 0)) { reach = Math.max(prev, p - WALL_STEP); blocked = true; break; }
      if (p >= target) break;
    }
    a.front = reach;
    let hit = false;
    if (reach > prev) {
      hit = g.combat.arrowSegment(a.x + a.dx * prev, a.z + a.dz * prev, a.x + a.dx * reach, a.z + a.dz * reach, a.width, a.mult, a.mark, a.source, a.pierce, a.finisher && !a.explode, a.launch);
    }
    if (hit) { this.end(a, g.combat.lastHitX, g.combat.lastHitZ, ArrowEndReason.Hit); return; }
    if (blocked) { this.end(a, a.x + a.dx * reach, a.z + a.dz * reach, ArrowEndReason.Wall); return; }
    if (reach >= a.range) this.end(a, a.x + a.dx * reach, a.z + a.dz * reach, ArrowEndReason.Range);
  }

  /** 화살 끝: 폭발·회오리를 남기고 Ev.ArrowEnd */
  private end(a: Arrow, x: number, z: number, reason: number): void {
    a.active = false;
    const g = this.g, slot = this.arrows.indexOf(a);
    const e = g.emit(Ev.ArrowEnd); e.x = x; e.z = z; e.a = slot; e.b = reason; e.c = a.kind;
    if (a.explode) {
      this.worldCue(a.skillId, 'impact', x, z);
      // 폭발은 새 표식(화살이 이미 맞힌 적도 폭발에 맞는다). 마무리 계열이면 넉백·띄우기 처치 반응
      const mk = a.mark;
      mk.mobs.fill(0); mk.boss = false;
      g.combat.zoneCircle(x, z, a.explode.radius, a.explode.mult * (a.mult > 0 ? a.mult : 1), mk, false, a.finisher);
    }
    if (a.vortex) this.placeAt(a.vortex, a.vortex.mult, a.skillId, x, z);
  }

  private advanceZone(zn: Zone): void {
    const g = this.g;
    if (zn.kind === ZoneKind.net) {
      // 덫: 몹이 밟을 때까지(또는 수명 끝까지) 기다린다
      if (--zn.life <= 0) { zn.active = false; return; }
      if (!this.netTriggered(zn)) return;
      this.worldCue(zn.skillId, 'snap', zn.x, zn.z);
      this.snapNet(zn);
      zn.active = false;
      return;
    }
    if (zn.kind === ZoneKind.vortex && zn.pull > 0) this.pullTo(zn);
    if (--zn.timer > 0) return;
    zn.timer = zn.every;
    zn.pulsesLeft--;
    const last = zn.pulsesLeft <= 0;
    this.worldCue(zn.skillId, 'pulse', zn.x, zn.z);
    // 다단: 매 판정마다 같은 대상을 다시 맞힐 수 있다(새 표식), 마지막 판정만 다단 중간타가 아니다
    const mk = zn.mark;
    mk.mobs.fill(0); mk.boss = false;
    g.combat.zoneCircle(zn.x, zn.z, zn.radius, zn.mult, mk, !last, false);
    if (last) zn.active = false;
  }

  /** 덫 밟힘: 밟힘 반경 안에 서기·추적·공격 중인 일반 몹(보스 제외)이 있다 */
  private netTriggered(zn: Zone): boolean {
    const m = this.g.mobs;
    for (let i = 0; i < m.capacity; i++) {
      const st = m.state[i];
      // 깨어 있는 몹(추적·선딜·공격·경직)만 덫을 밟는다. 잠든·떠 있는·누운·기상 무적 몹은 덫을 소모하지 않는다
      if (st !== MobState.Chase && st !== MobState.Windup && st !== MobState.Attack && st !== MobState.Stunned) continue;
      if (Math.hypot(m.x[i] - zn.x, m.z[i] - zn.z) <= zn.trigger + this.g.mobController.radius(i)) return true;
    }
    return false;
  }

  /** 덫 발동: 반경 안 몹 속박(엘리트 저항, 보스는 그로기 중에만 맞는다·속박은 없음) + 한 번 피해 */
  private snapNet(zn: Zone): void {
    const g = this.g, m = g.mobs;
    for (let i = 0; i < m.capacity; i++) {
      const st = m.state[i];
      if (st === MobState.Inactive || st === MobState.Dying || st === MobState.Spawning) continue;
      if (Math.hypot(m.x[i] - zn.x, m.z[i] - zn.z) > zn.radius + g.mobController.radius(i)) continue;
      const t = g.mobController.root(i, zn.root);
      const e = g.emit(Ev.MobRoot); e.x = m.x[i]; e.z = m.z[i]; e.a = i; e.b = t; e.c = 0;
    }
    // 보스는 속박되지 않으므로 Ev.MobRoot를 내지 않는다(그로기 중 덫 피해만 아래에서)
    const b = g.boss;
    const mk = zn.mark;
    mk.mobs.fill(0); mk.boss = false;
    // 보스는 그로기(Stagger) 중일 때만 덫 피해를 받는다
    if (b.state !== BossState.Stagger) mk.boss = true;
    g.combat.zoneCircle(zn.x, zn.z, zn.radius, zn.mult, mk, false, false);
  }

  /** 회오리: 반경 안 몹(보스 제외)을 중심으로 끈다. 중심에 닿으면 멈춘다 */
  private pullTo(zn: Zone): void {
    const g = this.g, m = g.mobs, step = zn.pull * FIXED_DT;
    for (let i = 0; i < m.capacity; i++) {
      const st = m.state[i];
      if (st === MobState.Inactive || st === MobState.Dying || st === MobState.Spawning) continue;
      const dx = zn.x - m.x[i], dz = zn.z - m.z[i], d = Math.hypot(dx, dz);
      if (d > zn.radius || d <= HERO.radius) continue;
      const move = Math.min(d - HERO.radius, step);
      g.moveMob(i, (dx / d) * move, (dz / d) * move, g.mobController.radius(i));
    }
  }

  /** 세계 위치 큐: 지금 영웅 스킬 표(전직 갈래 포함)에서 그 스킬의 at 'world'·event 큐를 모두 낸다(Ev.SkillCue flags 1, 갈래 1이면 | 2 — C10-6) */
  private worldCue(skillId: number, event: NonNullable<SkillCue['event']>, x: number, z: number): void {
    if (skillId <= 0) return;
    const g = this.g, job = g.jobRun.branch, tl = heroSkills(g.heroId, job)[skillId - 1];
    if (!tl) return;
    for (let k = 0; k < tl.cues.length; k++) {
      const c = tl.cues[k];
      if (c.at !== 'world' || c.event !== event) continue;
      const e = g.emit(Ev.SkillCue);
      e.x = x; e.y = 0; e.z = z; e.a = skillId; e.b = c.channel === 'vfx' ? 0 : c.channel === 'sfx' ? 1 : 2; e.c = k; e.d = g.hero.facing; e.flags = 1 | (job === 1 ? 2 : 0);
    }
  }
}
